<template>
  <div>

    <!-- ============================================================= -->
    <!-- CABECERA                                                       -->
    <!-- ============================================================= -->
    <wz-card shadow="sm" style="margin-bottom: 16px" :header-border="false">
      <div class="employee-header">

        <!-- Avatar con subida/cambio de foto -->
        <div class="employee-avatar-col">
          <div class="employee-avatar-wrap" @click="$refs.photoInput.click()">
            <wz-avatar :photo-url="photoObjectUrl" :name="fullName" size="lg" />
            <div class="employee-avatar-overlay">
              <i class="ri-camera-line"></i>
              <span>{{ employee.hasPhoto ? 'Cambiar foto' : 'Subir foto' }}</span>
            </div>
          </div>
          <a v-if="employee.hasPhoto" href="#" class="employee-avatar-delete" @click.prevent="openDeletePhoto">
            <i class="ri-delete-bin-line"></i> Eliminar foto
          </a>
          <wz-tooltip :text="employee.hasPhoto ? 'Descargar fotocheck PDF' : 'Debe subir una foto antes de generar el fotocheck'">
            <wz-button variant="outline" size="sm" :disabled="!employee.hasPhoto" @click="downloadFotocheck">
              <i class="ri-id-card-line mr-1"></i>Generar fotocheck
            </wz-button>
          </wz-tooltip>
          <input ref="photoInput" type="file" accept=".jpg,.jpeg,.png" style="display:none" @change="onPhotoSelected" />
        </div>

        <!-- Info principal -->
        <div class="employee-header-info">
          <h2 class="employee-full-name">{{ fullName }}</h2>
          <p class="employee-identifier">{{ identifierTypeLabel }}: {{ employee.identifier }}</p>
          <div class="employee-meta">
            <span>
              <i class="ri-building-line"></i>
              <small>Empresa:</small>
              <strong>{{ contractInfo('company') }}</strong>
            </span>
            <span>
              <i class="ri-team-line"></i>
              <small>Área:</small>
              <strong>{{ contractInfo('area') }}</strong>
            </span>
            <span>
              <i class="ri-briefcase-line"></i>
              <small>Cargo:</small>
              <strong>{{ contractInfo('position') }}</strong>
            </span>
            <span>
              <i class="ri-calendar-line"></i>
              <small>Ingreso:</small>
              <strong v-if="hireDates.length">
                <template v-for="(d, i) in hireDates"><span :key="i" v-local-date="d"></span><span v-if="i < hireDates.length - 1" :key="'sep-' + i">, </span></template>
              </strong>
              <strong v-else>---</strong>
            </span>
            <span v-if="isCeased && cessationDateDisplay">
              <i class="ri-logout-box-r-line"></i>
              <small>Cese:</small>
              <strong v-local-date="cessationDateDisplay"></strong>
            </span>
          </div>
        </div>

        <!-- Estado + acciones -->
        <div class="employee-header-actions">
          <wz-badge :variant="statusInfo.color" pill>
            {{ statusInfo.label }}
          </wz-badge>
          <wz-button
            variant="primary"
            @click="$router.push({ name: 'employee-edit', params: { id: String(employeeId) } })"
          >
            <i class="ri-edit-line"></i> Editar datos
          </wz-button>
          <wz-button
            variant="outline"
            @click="goToAuditHistory"
          >
            <i class="ri-history-line mr-1"></i> Ver historial de auditoría
          </wz-button>
        </div>
      </div>

      <!-- Alerta documentos pendientes -->
      <div v-if="hasMissingDocuments" class="employee-docs-alert">
        <wz-alert variant="danger">
          <div style="width:100%">
            <div style="display:flex; align-items:center; margin-bottom:8px">
              <i class="ri-lock-line" style="font-size:1.1rem; margin-right:8px; flex-shrink:0"></i>
              <strong>BLOQUEADO — Documentos obligatorios pendientes</strong>
              <a href="#" @click.prevent="goToDocumentsTab" style="margin-left:auto; color:inherit; white-space:nowrap; flex-shrink:0">Ir a Documentos →</a>
            </div>
            <div v-if="compliance && compliance.globalMissing && compliance.globalMissing.length" style="margin-bottom:4px">
              <strong>Globales:</strong> {{ compliance.globalMissing.join(', ') }}
            </div>
            <div v-if="compliance && compliance.byContract">
              <div v-for="c in compliance.byContract" :key="c.contractId" style="margin-bottom:4px">
                <strong>{{ c.companyName }} — {{ c.positionName }}:</strong>
                <span v-if="c.companyMissing && c.companyMissing.length"> Empresa: {{ c.companyMissing.join(', ') }}.</span>
                <span v-if="c.positionMissing && c.positionMissing.length"> Cargo: {{ c.positionMissing.join(', ') }}.</span>
              </div>
            </div>
          </div>
        </wz-alert>
      </div>
    </wz-card>

    <!-- ============================================================= -->
    <!-- TABS                                                           -->
    <!-- ============================================================= -->
    <wz-card :header-border="false" :body-padding="false">
      <wz-tabs :items="tabItems" v-model="activeTab" variant="underline" style="padding: 0 20px">

        <!-- Datos personales -->
        <template #personal>
          <div class="detail-section">
            <div class="detail-group">
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Tipo de documento</span>
                  <strong>{{ identifierTypeLabel || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Número de documento</span>
                  <strong>{{ employee.identifier || '---' }}</strong>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Apellido paterno</span>
                  <strong>{{ employee.lastNamePaternal || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Apellido materno</span>
                  <strong>{{ employee.lastNameMaternal || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Nombres</span>
                  <strong>{{ employee.name || '---' }}</strong>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Fecha de nacimiento</span>
                  <strong v-local-date="employee.birthDate"></strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Sexo</span>
                  <strong>{{ genderLabel }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Estado civil</span>
                  <strong>{{ employee.maritalStatus || '---' }}</strong>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Nacionalidad</span>
                  <strong>{{ employee.nationality || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Grupo sanguíneo</span>
                  <strong>{{ employee.bloodType || '---' }}</strong>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Discapacidad</span>
                  <strong>{{ employee.hasDisability ? 'Sí' : 'No' }}</strong>
                </div>
                <template v-if="employee.hasDisability">
                  <div class="detail-field">
                    <span class="detail-label">Tipo de discapacidad</span>
                    <strong>{{ employee.disabilityType || '---' }}</strong>
                  </div>
                  <div class="detail-field">
                    <span class="detail-label">Certificado CONADIS</span>
                    <strong>{{ employee.conadisCertificate || '---' }}</strong>
                  </div>
                </template>
              </div>
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Nivel educativo</span>
                  <strong>{{ employee.educationLevel || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Profesión</span>
                  <strong>{{ employee.profession || '---' }}</strong>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Contacto -->
        <template #contact>
          <div class="detail-section">
            <div class="detail-group">
              <div class="detail-row">
                <div class="detail-field w-1/6">
                  <span class="detail-label">Dirección</span>
                  <strong>{{ employee.address || '---' }}</strong>
                </div>
                <div class="detail-field w-1/6">
                  <span class="detail-label">Ubigeo domicilio</span>
                  <strong>{{ employee.ubigeoDescription || '---' }}</strong>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-field w-1/6">
                  <span class="detail-label">Ubigeo nacimiento</span>
                  <strong>{{ employee.birthUbigeoDescription || '---' }}</strong>
                </div>
                <div class="detail-field w-1/6">
                  <span class="detail-label">Teléfono personal</span>
                  <strong>{{ employee.personalPhone || '---' }}</strong>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-field w-1/6">
                  <span class="detail-label">Correo personal</span>
                  <strong>{{ employee.personalEmail || '---' }}</strong>
                </div>
              </div>
            </div>

            <div class="detail-subtitle">Contacto de emergencia</div>
            <div class="detail-group">
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Nombre</span>
                  <strong>{{ employee.emergencyContact || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Parentesco</span>
                  <strong>{{ employee.emergencyContactRelationship || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Teléfono</span>
                  <strong>{{ employee.emergencyPhoneNumber || '---' }}</strong>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Banco y pensiones -->
        <template #bank>
          <div class="detail-section">
            <div class="detail-subtitle">Cuenta de haberes</div>
            <div class="detail-group">
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Banco</span>
                  <strong>{{ employee.bank ? employee.bank.name : '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Tipo de cuenta</span>
                  <strong>{{ accountTypeLabel(employee.accountType) }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Número de cuenta</span>
                  <strong>{{ employee.bankAccount || '---' }}</strong>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">CCI</span>
                  <strong>{{ employee.bankInterAccount || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Moneda</span>
                  <strong>{{ employee.salaryCurrency || '---' }}</strong>
                </div>
              </div>
            </div>

            <div class="detail-subtitle">Cuenta CTS</div>
            <div class="detail-group">
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Banco CTS</span>
                  <strong>{{ employee.ctsBank ? employee.ctsBank.name : '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Número cuenta CTS</span>
                  <strong>{{ employee.ctsAccount || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Moneda CTS</span>
                  <strong>{{ employee.ctsCurrency || '---' }}</strong>
                </div>
              </div>
            </div>

            <div class="detail-subtitle">Sistema pensionario</div>
            <div class="detail-group">
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Sistema</span>
                  <strong>{{ employee.pensionSystem || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Entidad AFP</span>
                  <strong>{{ employee.pensionEntity ? employee.pensionEntity.name : '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">Código CUSPP</span>
                  <strong>{{ employee.cusppCode || '---' }}</strong>
                </div>
              </div>
              <div class="detail-row">
                <div class="detail-field">
                  <span class="detail-label">Tipo comisión AFP</span>
                  <strong>{{ employee.afpCommissionType || '---' }}</strong>
                </div>
                <div class="detail-field">
                  <span class="detail-label">5ta categoría</span>
                  <strong>{{ employee.fifthCategoryStatus || '---' }}</strong>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Familia -->
        <template #family>
          <FamilyTab v-if="employeeId" :employee-id="employeeId"
                     :employee-paternal-surname="employee.lastNamePaternal || ''"
                     :employee-gender="employee.gender" />
        </template>

        <!-- Contratos vigentes -->
        <template #contracts>
          <ContractSummaryTab :employee-id="employeeId" :current-contracts="activeContracts" />
        </template>

        <!-- Historial laboral -->
        <template #employment>
          <EmploymentHistoryTab :periods="employee.employmentPeriods || []" />
        </template>

        <!-- Documentos -->
        <template #documents>
          <DocumentsTab
            :employee-id="employeeId"
            :cessation-documents="employee.cessationDocuments || []"
            :cessation-contracts="employee.cessationContracts || []"
            @compliance-changed="loadCompliance(employeeId)"
            @cessation-uploaded="load(employeeId)"
          />
        </template>

        <!-- Vacaciones -->
        <template #vacations>
          <VacationsTab :employee-id="employeeId" :contracts="employee.currentContracts || []" />
        </template>

        <!-- Descansos médicos -->
        <template #medical>
          <MedicalLeavesTab :employee-id="employeeId" :contracts="employee.currentContracts || []" />
        </template>

        <!-- Asistencia -->
        <template #attendance>
          <AttendanceTab :employee-id="employeeId" :current-contracts="activeContracts" />
        </template>

        <!-- Desc. y adicionales -->
        <template #movements>
          <PayrollMovementsTab :employee-id="employeeId" />
        </template>

        <!-- Boletas de pago -->
        <template #payslips>
          <PayslipsTab v-if="employeeId" :employee-id="employeeId" />
        </template>

      </wz-tabs>
    </wz-card>

    <!-- Modal: preview + confirmar subida de foto -->
    <wz-modal v-model="photoModal.show" title="Actualizar foto">
      <div class="text-center">
        <img
          v-if="photoModal.previewUrl"
          :src="photoModal.previewUrl"
          style="max-width: 200px; max-height: 267px; object-fit: cover; border-radius: 8px;"
          alt="Vista previa"
        />
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="photoModal.show = false">Cancelar</wz-button>
        <wz-button variant="primary" :disabled="photoModal.saving" @click="confirmPhotoUpload">
          {{ photoModal.saving ? 'Subiendo...' : 'Confirmar' }}
        </wz-button>
      </template>
    </wz-modal>

    <!-- Modal: confirmar eliminación de foto -->
    <wz-modal v-model="deletePhotoModal" title="Eliminar foto">
      <p>¿Está seguro que desea eliminar la foto del colaborador?</p>
      <template #footer>
        <wz-button variant="outline-secondary" @click="deletePhotoModal = false">Cancelar</wz-button>
        <wz-button variant="danger" @click="confirmDeletePhoto">Eliminar</wz-button>
      </template>
    </wz-modal>

  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import { Constants as LocalConstants } from '@/core/utils'
import { EmployeeService } from '@/core/services'
import fileDownload from 'js-file-download'

import FamilyTab           from './tabs/FamilyTab.vue'
import ContractSummaryTab  from './tabs/ContractSummaryTab.vue'
import DocumentsTab        from './tabs/DocumentsTab.vue'
import VacationsTab        from './tabs/VacationsTab.vue'
import MedicalLeavesTab    from './tabs/MedicalLeavesTab.vue'
import AttendanceTab       from './tabs/AttendanceTab.vue'
import PayrollMovementsTab from './tabs/PayrollMovementsTab.vue'
import PayslipsTab         from './tabs/PayslipsTab.vue'
import EmploymentHistoryTab from './tabs/EmploymentHistoryTab.vue'

export default {
  name: 'EmployeeDetails',
  components: {
    FamilyTab, ContractSummaryTab, DocumentsTab,
    VacationsTab, MedicalLeavesTab, AttendanceTab, PayrollMovementsTab,
    PayslipsTab, EmploymentHistoryTab,
  },

  data() {
    return {
      employee:  {},
      compliance: null,
      activeTab: 'personal',
      photoObjectUrl: null,
      photoModal: { show: false, file: null, previewUrl: null, saving: false },
      deletePhotoModal: false,
    }
  },

  computed: {
    tabItems() {
      const contractLabel = this.activeContracts.length > 1
        ? `Contratos vigentes (${this.activeContracts.length})`
        : 'Contratos vigentes'
      return [
        { key: 'personal',   label: 'Datos personales',   icon: 'ri-user-line' },
        { key: 'contact',    label: 'Contacto',            icon: 'ri-map-pin-line' },
        { key: 'bank',       label: 'Banco y pensiones',   icon: 'ri-bank-card-line' },
        { key: 'family',     label: 'Familia',             icon: 'ri-parent-line' },
        { key: 'contracts',  label: contractLabel,         icon: 'ri-file-list-line' },
        { key: 'employment', label: 'Historial laboral',   icon: 'ri-history-line' },
        { key: 'documents',  label: 'Documentos',          icon: 'ri-file-text-line' },
        { key: 'vacations',  label: 'Vacaciones',          icon: 'ri-calendar-check-line' },
        { key: 'medical',    label: 'Descansos médicos',   icon: 'ri-heart-pulse-line' },
        { key: 'attendance', label: 'Asistencia',          icon: 'ri-time-line' },
        { key: 'movements',  label: 'Desc. y adicionales', icon: 'ri-exchange-funds-line' },
        { key: 'payslips',   label: 'Boletas',             icon: 'ri-file-pdf-2-line' },
      ]
    },
    employeeId()    { return this.$route.params.id ? Number(this.$route.params.id) : null },
    fullName()      { return this.employee.fullName || '-' },
    initials() {
      const n = (this.employee.name || '').trim()
      const l = (this.employee.lastNamePaternal || '').trim()
      return ((n[0] || '') + (l[0] || '')).toUpperCase() || '?'
    },
    identifierTypeLabel() {
      const val = Util.searchValue(this.employee.identifierType, LocalConstants.IdentifierType)
      return val ? val.label : (this.employee.identifierType || '---')
    },
    genderLabel() {
      const g = this.employee.gender
      if (g === 1 || g === '1') return 'MASCULINO'
      if (g === 2 || g === '2') return 'FEMENINO'
      return g || '---'
    },
    activeContracts() { return Array.isArray(this.employee.currentContracts) ? this.employee.currentContracts : [] },
    hasContract()     { return this.activeContracts.length > 0 },
    // Badge de estado del header: usa el status efectivo que devuelve el backend (ACTIVO / VENCIDO /
    // SIN CONTRATO / CESADO), coherente con la lista. Fallback a SIN CONTRATO si aún no cargó.
    statusInfo() {
      const map = {
        'ACTIVO':       { label: 'ACTIVO',       color: 'success-solid' },
        'VENCIDO':      { label: 'VENCIDO',      color: 'warning-solid' },
        'CESADO':       { label: 'CESADO',       color: 'dark' },
        'SIN CONTRATO': { label: 'SIN CONTRATO', color: 'secondary-solid' },
      }
      return map[this.employee && this.employee.displayStatus]
        || { label: 'SIN CONTRATO', color: 'secondary-solid' }
    },
    hasMissingDocuments() { return this.compliance != null && !this.compliance.compliant },
    isCeased() { return this.employee.displayStatus === 'CESADO' },
    employmentPeriods() { return Array.isArray(this.employee.employmentPeriods) ? this.employee.employmentPeriods : [] },
    // Ingreso (solo lectura): hire_date de los períodos abiertos (VIGENTE o VENCIDO, sin cese).
    hireDates() {
      return [...new Set(
        this.employmentPeriods.filter(p => p.displayStatus !== 'CERRADO' && p.hireDate).map(p => p.hireDate),
      )]
    },
    // Cese: la fecha de cese más reciente de los períodos cerrados (CERRADO).
    cessationDateDisplay() {
      const dates = this.employmentPeriods
        .filter(p => p.displayStatus === 'CERRADO' && p.cessationDate)
        .map(p => p.cessationDate)
        .sort()
      return dates.length ? dates[dates.length - 1] : null
    },
  },

  methods: {
    // DEF-017 #5: mapea el key del tipo de cuenta (1/2/3) a su etiqueta (AHORROS/CORRIENTE/SUELDO).
    accountTypeLabel(val) {
      if (val == null || val === '') return '---'
      const match = Object.values(LocalConstants.AccountType).find(t => t.key === Number(val))
      return match ? match.label : val
    },
    goToDocumentsTab() { this.activeTab = 'documents' },
    // Navega a Auditoría con el filtro "ver por entidad" pre-cargado para este colaborador.
    goToAuditHistory() {
      this.$router.push({
        name: 'audit-log',
        query: { entityType: 'Employee', entityId: this.employeeId },
      })
    },
    contractInfo(field) {
      const list = this.activeContracts
      if (list.length === 0) return '---'
      const extract = (c) => {
        if (field === 'company')   return (c.company && c.company.name) || c.companyName || null
        if (field === 'area')      return (c.area && c.area.description) || c.areaName || null
        if (field === 'position')  return (c.position && c.position.description) || c.positionName || null
        if (field === 'startDate') return c.startDate || null
        return null
      }
      if (field === 'startDate') {
        const dates = list.map(extract).filter(Boolean).sort()
        return dates[0] || '---'
      }
      const values = list.map(extract).filter(Boolean)
      return values.length ? [...new Set(values)].join(', ') : '---'
    },
    async load(id) {
      try {
        this.$store.dispatch('app/loading', true)
        const response = await EmployeeService.get(id)
        this.employee = response.payload
        await this.loadPhoto()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el colaborador')
        this.$router.push({ name: 'employee-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    // Carga la foto autenticada (bytes → objectURL) para pasarla al avatar.
    async loadPhoto() {
      if (this.photoObjectUrl) { URL.revokeObjectURL(this.photoObjectUrl); this.photoObjectUrl = null }
      if (!this.employee.hasPhoto) return
      try {
        const blob = await EmployeeService.getPhoto(this.employeeId)
        this.photoObjectUrl = URL.createObjectURL(blob)
      } catch (e) { /* sin foto */ }
    },

    async loadCompliance(id) {
      try {
        const response = await EmployeeService.getCompliance(id)
        this.compliance = response.payload
      } catch (e) {
        console.error(e)
      }
    },

    onPhotoSelected(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const ext = (file.name.split('.').pop() || '').toLowerCase()
      if (!['jpg', 'jpeg', 'png'].includes(ext)) {
        Alert.error('Formato no permitido. Use JPG, JPEG o PNG.')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('La imagen no debe superar 5MB.')
        return
      }
      this.photoModal = { show: true, file, previewUrl: URL.createObjectURL(file), saving: false }
    },
    async confirmPhotoUpload() {
      if (!this.photoModal.file) return
      try {
        this.photoModal.saving = true
        await EmployeeService.uploadPhoto(this.employeeId, this.photoModal.file)
        this.photoModal.show = false
        await this.load(this.employeeId)
        Alert.success('Foto actualizada correctamente')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al subir la foto')
      } finally {
        this.photoModal.saving = false
      }
    },
    openDeletePhoto() {
      this.deletePhotoModal = true
    },
    async downloadFotocheck() {
      if (!this.employee.hasPhoto) return
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await EmployeeService.downloadFotocheck(this.employeeId)
        fileDownload(resp.data, `fotocheck-${this.employee.code || this.employeeId}.pdf`)
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al generar el fotocheck')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmDeletePhoto() {
      try {
        this.$store.dispatch('app/loading', true)
        await EmployeeService.deletePhoto(this.employeeId)
        this.deletePhotoModal = false
        await this.load(this.employeeId)
        Alert.success('Foto eliminada')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al eliminar la foto')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },

  created() {
    const id = this.$route.params.id
    this.load(id)
    this.loadCompliance(id)
    // Permite aterrizar en un tab específico vía ?tab= (p. ej. al volver desde un formulario).
    const tab = this.$route.query.tab
    if (tab && this.tabItems.some(t => t.key === tab)) this.activeTab = tab
  },
  beforeDestroy() {
    if (this.photoObjectUrl) URL.revokeObjectURL(this.photoObjectUrl)
  },
}
</script>

<style lang="stylus" scoped>
/* ---- Avatar ---- */
.employee-avatar
  width 64px
  height 64px
  border-radius 50%
  background-color var(--admin-primary-color, #0288D1)
  color white
  font-size 1.3rem
  font-weight 700
  display flex
  align-items center
  justify-content center
  flex-shrink 0

.employee-avatar-col
  display flex
  flex-direction column
  align-items center
  gap 6px
  flex-shrink 0
.employee-avatar-wrap
  position relative
  cursor pointer
  border-radius 8px
  overflow hidden
.employee-avatar-overlay
  position absolute
  inset 0
  display flex
  flex-direction column
  align-items center
  justify-content center
  gap 2px
  background rgba(0, 0, 0, 0.45)
  color #fff
  font-size 0.75rem
  opacity 0
  transition opacity .15s
.employee-avatar-wrap:hover .employee-avatar-overlay
  opacity 1
.employee-avatar-delete
  font-size 0.78rem
  color var(--admin-danger-color, #dc2626)
  cursor pointer

/* ---- Header layout ---- */
.employee-header
  display flex
  align-items flex-start
  gap 20px
  flex-wrap wrap

.employee-header-info
  flex 1
  min-width 0

.employee-full-name
  font-size 1.2rem
  font-weight 700
  color var(--admin-text-main)
  margin 0 0 4px

.employee-identifier
  font-size 13px
  color var(--admin-text-muted)
  margin 0 0 10px

.employee-meta
  display flex
  flex-wrap wrap
  gap 16px
  font-size 13px

  span
    display flex
    align-items center
    gap 5px
    color var(--admin-text-muted)

    strong
      color var(--admin-text-main)

.employee-header-actions
  display flex
  flex-direction column
  align-items flex-end
  gap 10px
  flex-shrink 0

.employee-docs-alert
  margin-top 16px
  padding-top 0

/* ---- Detail sections ---- */
.detail-section
  padding 20px

.detail-subtitle
  font-size 13px
  font-weight 600
  color var(--admin-text-muted)
  text-transform uppercase
  letter-spacing 0.04em
  margin-bottom 12px
  padding-bottom 6px
  border-bottom 1px solid var(--admin-border-color)

.detail-group
  margin-bottom 24px

.detail-row
  display grid
  grid-template-columns repeat(3, 1fr)
  gap 16px 24px
  margin-bottom 16px

  &:last-child
    margin-bottom 0

.detail-field
  display flex
  flex-direction column
  gap 3px

  &.col-2
    grid-column span 1

.detail-label
  font-size 12px
  color var(--admin-text-muted)

.detail-field strong
  font-size 14px
  color var(--admin-text-main)
  font-weight 600
</style>
